/**
 * The skills catalog as people browse it: each category with its skills under
 * it, then whatever sits in no category.
 *
 * Shared by the demand's chip picker, the profile's picker and the catalog
 * panel, so all three group and search alike.
 */

import type { Capability } from '../types/capability'
import { skillKey } from './skillMatch'

/** The hover on a category, wherever it is offered or held. */
export const WHOLE_CATEGORY_NOTE = 'Whole category: any skill in it counts.'

export interface SkillGroup {
  /** The category's own entry, itself pickable. `null` for the uncategorised rest. */
  category: Capability | null
  skills: Capability[]
}

export function hasCategories(catalog: Capability[]): boolean {
  return catalog.some((c) => c.is_category)
}

const byName = (a: Capability, b: Capability) => a.name.localeCompare(b.name)

/**
 * Categories by name, each with its skills by name; entries in no category
 * last, in the order given. With no categories at all that is one group
 * holding everything — a flat list, as before categories existed.
 */
export function groupCatalog(catalog: Capability[]): SkillGroup[] {
  const groups = catalog
    .filter((c) => c.is_category)
    .sort(byName)
    .map((category) => ({ category, skills: [] as Capability[] }))
  const byId = new Map(groups.map((g) => [g.category.id, g] as const))
  const byKey = new Map(groups.map((g) => [skillKey(g.category.name), g] as const))
  const rest: Capability[] = []
  for (const c of catalog) {
    if (c.is_category) continue
    const home =
      (c.parent_id != null ? byId.get(c.parent_id) : undefined) ??
      (c.category ? byKey.get(skillKey(c.category)) : undefined)
    if (home) home.skills.push(c)
    else rest.push(c)
  }
  for (const g of groups) g.skills.sort(byName)
  const out: SkillGroup[] = [...groups]
  if (rest.length > 0) out.push({ category: null, skills: rest })
  return out
}

/**
 * How well an entry answers a search, best first: the name exactly, then
 * starting with it, then containing it (the server's order), then a hit only
 * in its description, examples or category. `null` = no hit. Not the code:
 * nobody sees it, and "CAT-01" would answer every search for "cat".
 */
function rank(c: Capability, q: string, categoryName: string | undefined): number | null {
  const name = c.name.toLowerCase()
  if (name === q) return 0
  if (name.startsWith(q)) return 1
  if (name.includes(q)) return 2
  const elsewhere = [c.description, c.examples, categoryName]
  return elsewhere.some((f) => !!f && f.toLowerCase().includes(q)) ? 3 : null
}

/**
 * The groups that answer `query`. A skill is searched on its name, description,
 * examples and category, so a category whose name matches keeps all its
 * skills. The best hits lead within a group, and the group holding the best hit
 * comes first. An empty query returns the groups as they are.
 */
export function searchCatalog(groups: SkillGroup[], query: string): SkillGroup[] {
  const q = query.trim().toLowerCase()
  if (!q) return groups
  const hits: { group: SkillGroup; best: number }[] = []
  for (const g of groups) {
    const skills = g.skills
      .map((s) => ({ s, r: rank(s, q, g.category?.name) }))
      .filter((x): x is { s: Capability; r: number } => x.r !== null)
      .sort((a, b) => a.r - b.r || byName(a.s, b.s))
    const own = g.category ? rank(g.category, q, undefined) : null
    if (own === null && skills.length === 0) continue
    const best = Math.min(own ?? 4, ...skills.map((x) => x.r))
    hits.push({ group: { category: g.category, skills: skills.map((x) => x.s) }, best })
  }
  // Stable, so equal groups keep their order: categories by name, the rest last.
  return hits.sort((a, b) => a.best - b.best).map((x) => x.group)
}

/** A skill's description and examples, for a hover. */
export function skillHoverText(c: Capability): string | undefined {
  const lines = [c.description, c.examples ? `Examples: ${c.examples}` : null].filter(Boolean)
  return lines.length > 0 ? lines.join('\n') : undefined
}
